import React, { useState } from 'react';
import { StarFilledIcon } from '@domain-group/fe-co-icon';
import RmaDisclaimer from '../search-results/components/rma-disclaimer/rma-disclaimer';
import RmaLogo from '../shared/recommendations/rma-logo';
import {
  isStarRatingDesignApplicable,
  ProfileTier,
  DepthTier,
} from '../../utils/recommendations';
import ReviewsList from './reviews-list';
import * as styles from './agent-profile-reviews.style';
import type { RecommendationPayload } from '../../@types/recommendations';

export type AgentProfileReviewsProps = {
  contactId: number;
  agentFirstName: string;
  profileTier?: ProfileTier | string | null;
  depthTier?: DepthTier | string | null;
  overallStarRating?: number | null;
  totalReviews?: number | null;
  showStarRating?: boolean;
  onPageRendered?: (data: RecommendationPayload) => HTMLImageElement;
  onVendorRecoViewAll?: () => void;
  onVendorRecoShowMore?: () => void;
};

const AgentProfileReviews = ({
  contactId,
  agentFirstName,
  profileTier,
  depthTier,
  overallStarRating,
  totalReviews,
  showStarRating = true,
  onPageRendered,
  onVendorRecoViewAll,
  onVendorRecoShowMore,
}: AgentProfileReviewsProps): JSX.Element | null => {
  const [listUnavailable, setListUnavailable] = useState(false);
  const isApplicableTier = isStarRatingDesignApplicable({
    profileTier: profileTier as ProfileTier,
    depthTier: depthTier as DepthTier,
  });
  const isPlatinum = profileTier === 'platinum';
  const hasReviews = Boolean(overallStarRating) && Boolean(totalReviews);

  if (!isApplicableTier || !hasReviews || listUnavailable) {
    return null;
  }

  const headingText = agentFirstName
    ? `${agentFirstName}'s reviews`
    : 'Reviews';

  return (
    <section css={styles.section} data-testid="agent-profile-reviews">
      <h2 css={styles.heading}>
        <span css={styles.headingRow}>
          <span css={styles.headingName}>{headingText}</span>
          {showStarRating && (
            <span css={styles.ratingItem}>
              <span css={[styles.mutedBodyText, styles.dotSeparator]}>·</span>
              <span css={styles.ratingAndCount}>
                <StarFilledIcon css={styles.headingStar} />
                <span css={styles.mutedBodyText}>
                  {overallStarRating?.toFixed(1)}
                </span>
                <span css={styles.mutedBodyText}>
                  {`(${totalReviews} ${totalReviews === 1 ? 'review' : 'reviews'})`}
                </span>
                <span css={styles.pipeSeparator}>|</span>
                <RmaLogo
                  height={16}
                  marginLeft={0}
                  marginBottom={2}
                  marginTop={0}
                />
              </span>
            </span>
          )}
        </span>
      </h2>
      <RmaDisclaimer />
      <ReviewsList
        contactId={contactId}
        isPlatinum={isPlatinum}
        onUnavailable={() => setListUnavailable(true)}
        onPageRendered={onPageRendered}
        onVendorRecoViewAll={onVendorRecoViewAll}
        onVendorRecoShowMore={onVendorRecoShowMore}
      />
    </section>
  );
};

export default AgentProfileReviews;
